<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <p class="modal-card-title">Criteria Guide</p>
      <button @click="closeModal" type="button" class="close-icon">
        <i class="mdi mdi-close"></i>
      </button>
    </header>
    <section class="modal-card-body padding-bottom82">
      <p :class="$style.contentHeading">
        Add a guide to your criteria so data collectors can collect data easily
        and accurately.
      </p>
      <div :class="$style.criteriaDetail">
        <p :class="$style.header">Criteria</p>
        <Card :class="$style.criteriaDetailCard">
          <div
            v-if="
              currentCriteria.criteriaDetails &&
              currentCriteria.criteriaDetails.connectPlusLink !== ''
            "
            :class="$style.cplusId"
          >
            {{ currentCriteria.criteriaDetails.connectPlusLink }}
          </div>
          <div :class="$style.title">
            {{ currentCriteria.title }}
          </div>
          <div :class="$style.optionsContainer">
            <template v-if="currentCriteria.type === CRITERION_TYPE.BOOLEAN">
              <div
                v-for="(
                  booleanOption, booleanIndex
                ) in currentCriteria.booleanOptions"
                :key="booleanIndex"
              >
                <b-radio disabled size="is-small" native-value="booleanOption">
                  <span :class="$style.optionText">{{ booleanOption }}</span>
                </b-radio>
              </div>
            </template>
            <template v-if="currentCriteria.type === CRITERION_TYPE.RANGE">
              <div :class="$style.rangeOptionContainer">
                <span :class="$style.optionTitle">Min:</span>
                <span>{{ currentCriteria.range[0] }}</span>
              </div>
              <div :class="$style.rangeOptionContainer">
                <span :class="$style.optionTitle">Max:</span>
                <span>{{ currentCriteria.range[1] }}</span>
              </div>
              <div :class="$style.rangeOptionContainer">
                <span :class="$style.optionTitle">N/A:</span>
                <span>{{ currentCriteria.range[2] }}</span>
              </div>
            </template>
            <template v-if="currentCriteria.type === CRITERION_TYPE.CHECKBOXES">
              <div
                v-for="(
                  checkboxesOption, checkboxesOptionIndex
                ) in currentCriteria.checkboxesOptions"
                :key="checkboxesOptionIndex"
              >
                <b-checkbox disabled size="is-small" type="is-info">
                  <span :class="$style.optionText">{{ checkboxesOption }}</span>
                </b-checkbox>
              </div>
            </template>
            <template
              v-if="currentCriteria.type === CRITERION_TYPE.CHECKBOXES_SINGLE"
            >
              <div
                v-for="(
                  checkboxesOption, checkboxesOptionIndex
                ) in currentCriteria.checkboxesOptions"
                :key="checkboxesOptionIndex"
              >
                <b-checkbox disabled size="is-small" type="is-info">
                  <span :class="$style.optionText">{{ checkboxesOption }}</span>
                </b-checkbox>
              </div>
            </template>
          </div>
          <div :class="$style.tags" v-if="currentCriteria.tags">
            <span
              v-for="(tag, tagIndex) in getTags(currentCriteria.tags)"
              :key="tagIndex"
            >
              <span :class="$style.tagField">
                {{ tag.charAt(0).toUpperCase() + tag.slice(1) }}
              </span>
            </span>
          </div>
        </Card>
      </div>
      <div :class="$style.guideContainer">
        <p :class="$style.header">Guide</p>
        <b-input
          v-model="criteriaGuide"
          :class="$style.guideInput"
          type="textarea"
        />
        <button :class="$style.clearBtn" @click="clearCriteriaGuide">
          Clear
        </button>
      </div>
    </section>
    <footer class="modal-card-foot">
      <button
        class="button is-primary is-rounded btn-site-properties"
        @click="saveCriteriaGuide"
      >
        Save
      </button>
    </footer>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { cloneDeep as _cloneDeep, get as _get } from 'lodash';
import { CplusCriteria } from '@/store/modules/projects/types/projects.types';
import Card from '@/components/Card.vue';
import { RootState } from '@/store/store';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { ToastProgrammatic as Toast } from 'buefy';
import { AxiosError } from 'axios';
import { CRITERION_TYPE } from '@/store/types/criterions.types';

@Component({
  components: {
    Card,
  },
  data: () => {
    return {
      CRITERION_TYPE,
    };
  },
})
export default class CriteriaGuideModal extends Vue {
  @Prop()
  public currentCriteria!: CplusCriteria;
  @Prop()
  public criteriaIndex!: number;
  @Prop({ default: null })
  public projectId!: number;
  @Prop({ default: null })
  public auditId!: number;
  @Prop({ default: false })
  public isCreateProject!: boolean;

  public criteriaGuide: string = '';
  public dirtyCurrentCriteria!: CplusCriteria;

  @Action('projects/updateProjectCriteriaGuide')
  public updateProjectCriteriaGuide!: (params: any) => void;

  @Action('audits/updateAuditCriteriaGuide')
  public updateAuditCriteriaGuide!: (params: any) => void;

  @State((state: RootState) => state.projects.apiState.getProjectCriteria)
  public getProjectCriteriaState!: any;

  @State((state: RootState) => state.audits.apiState.getAuditCriteria)
  public getAuditCriteriaState!: any;

  @Watch('getProjectCriteriaState.success')
  @isTruthy
  public onGetProjectCriteriaSuccess() {
    Toast.open({
      type: 'is-dark',
      position: 'is-top',
      message: `Changes saved`,
    });
    this.$emit('close');
  }

  @Watch('getProjectCriteriaState.error')
  @isTruthy
  public onGetProjectCriteriaError(error: AxiosError) {
    Toast.open({
      queue: true,
      type: 'is-danger',
      position: 'is-top',
      message: _get(error, 'response.data.message', 'Error'),
    });
    this.$emit('close');
  }

  @Watch('getAuditCriteriaState.success')
  @isTruthy
  public onGetAuditCriteriaSuccess() {
    Toast.open({
      type: 'is-dark',
      position: 'is-top',
      message: `Changes saved`,
    });
    this.$emit('close');
  }

  @Watch('getAuditCriteriaState.error')
  @isTruthy
  public onGetAuditCriteriaError(error: AxiosError) {
    Toast.open({
      queue: true,
      type: 'is-danger',
      position: 'is-top',
      message: _get(error, 'response.data.message', 'Error'),
    });
    this.$emit('close');
  }

  public mounted() {
    this.dirtyCurrentCriteria = _cloneDeep(this.currentCriteria);
    this.criteriaGuide = this.dirtyCurrentCriteria.guide;
  }

  public saveCriteriaGuide() {
    this.dirtyCurrentCriteria.guide = this.criteriaGuide;
    if (this.isCreateProject) {
      this.$emit('updateCriteriaGuide', this.dirtyCurrentCriteria);
      this.$emit('close');
    } else {
      if (this.projectId) {
        this.updateProjectCriteriaGuide({
          projectId: this.projectId,
          criteriaId: this.dirtyCurrentCriteria.criteriaDetails.id,
          guide: this.criteriaGuide,
        });
      } else {
        this.updateAuditCriteriaGuide({
          auditId: this.auditId,
          criteriaId: this.dirtyCurrentCriteria.criteriaDetails.id,
          guide: this.criteriaGuide,
        });
      }
    }
  }

  public clearCriteriaGuide() {
    this.criteriaGuide = '';
  }

  public getTags(tagInfo: any) {
    if (Array.isArray(tagInfo)) {
      return tagInfo;
    } else {
      const tags: string[] = [];
      Object.keys(tagInfo).forEach((key: string) => {
        const tag = tagInfo[key];
        tag.forEach((value: string) => {
          tags.push(value);
        });
      });
      return tags;
    }
  }

  public closeModal() {
    this.$emit('close');
  }
}
</script>
<style lang="scss" module>
.contentHeading {
  color: #444f51;
  margin-bottom: 13px;
}
.criteriaDetail {
  .header {
    color: #9f9f9f;
    font-size: 14px;
    margin-bottom: 6px;
  }
  .criteriaDetailCard {
    padding: 16px 18px 16px 18px;
    flex-direction: column;
    background-color: #ffffff;
    box-shadow: none;
    margin-bottom: 25px;
    .cplusId {
      font-size: 12px;
      color: #9a9a9a;
      margin-bottom: 5px;
    }
    .title {
      font-size: 14px;
      font-weight: bold;
      color: #444f51;
      margin-bottom: 5px;
    }
    .tags {
      display: flex;
      flex-direction: row;
      color: #9a9a9a;
      margin-top: 5px;
      .tagField {
        margin-right: 15px;
        font-size: 12px;
      }
    }
  }
}
.guideContainer {
  .header {
    color: #9f9f9f;
    font-size: 14px;
    margin-bottom: 6px;
  }
  .guideInput {
    margin-bottom: 10px;
    font-size: 16px;
    color: #444f51;
    text-align: left;
    .textarea {
      padding: 10px 12px 10px 12px;
    }
  }
  .clearBtn {
    background: none;
    border: none;
    color: #ff7171;
    font-size: 14px;
    font-weight: bold;
    &:hover {
      cursor: pointer;
    }
  }
}
.optionText {
  color: #444f51 !important;
  font-size: 14px;
  line-height: 17px;
  font-weight: 400;
  text-wrap: nowrap;
}
.rangeOptionContainer {
  display: flex;
  flex-direction: row;
  font-size: 14px;
  color: #444f51 !important;
  .optionTitle {
    margin-right: 5px;
  }
}
</style>
